import React, { useEffect, useState } from 'react';
import { Button, useToast } from '@chakra-ui/react';
import { FaGoogle } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { handleSuccessfulAuth } from '../utils/auth';

const GoogleSignInLegacy = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Load Google Identity Services script
    const loadGoogleScript = () => {
      // Check if script is already loaded
      if (window.google?.accounts?.id) {
        setIsGoogleLoaded(true);
        initializeGoogleSignIn();
        return;
      }

      // Remove any existing script
      const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsGoogleLoaded(true);
        initializeGoogleSignIn();
      };
      script.onerror = () => {
        console.error('Failed to load Google Identity Services');
        toast({
          title: 'Error',
          description: 'Failed to load Google Sign-In. Please check your internet connection.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      };
      document.head.appendChild(script);
    };

    loadGoogleScript();

    // Cleanup function
    return () => {
      const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const initializeGoogleSignIn = () => {
    if (window.google?.accounts?.id && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
        });
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
      }
    }
  };

  const handleGoogleResponse = async (response) => {
    setIsLoading(true);
    
    try {
      // Send the Google token to your backend for verification and user creation/login
      const backendResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/google`,
        {
          token: response.credential,
        },
        { 
          withCredentials: true,
          timeout: 10000
        }
      );

      if (backendResponse?.data?.accessToken) {
        // Use the enhanced auth utility function
        await handleSuccessfulAuth(backendResponse.data, navigate, toast);
      } else {
        throw new Error('No access token received from server');
      }
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      toast({
        title: 'Sign-In Failed',
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      toast({
        title: 'Configuration Error',
        description: 'Google Sign-In is not configured. Please contact support.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback: create a temporary button and click it
            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'absolute';
            tempDiv.style.top = '-9999px';
            tempDiv.style.left = '-9999px';
            document.body.appendChild(tempDiv);

            window.google.accounts.id.renderButton(tempDiv, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'signin_with',
              width: 250,
            });

            setTimeout(() => {
              const button = tempDiv.querySelector('div[role="button"]');
              if (button) {
                button.click();
              }
              document.body.removeChild(tempDiv);
            }, 100);
          }
        });
      } catch (error) {
        console.error('Error prompting Google Sign-In:', error);
        toast({
          title: 'Error',
          description: 'Failed to open Google Sign-In. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
    } else {
      toast({
        title: 'Error',
        description: 'Google Sign-In is not available. Please refresh the page and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return (
      <Button
        leftIcon={<FaGoogle />}
        colorScheme="red"
        variant="outline"
        width="full"
        isDisabled={true}
        _disabled={{
          opacity: 0.6,
          cursor: 'not-allowed',
        }}
      >
        Google Sign-In Not Configured
      </Button>
    );
  }

  return (
    <Button
      leftIcon={<FaGoogle />}
      colorScheme="red"
      variant="outline"
      width="full"
      onClick={handleGoogleSignIn}
      isLoading={isLoading}
      isDisabled={!isGoogleLoaded || isLoading}
      loadingText="Signing in..."
      _hover={{
        bg: 'red.50',
        borderColor: 'red.300',
      }}
      _disabled={{
        opacity: 0.6,
        cursor: 'not-allowed',
      }}
    >
      Sign in with Google
    </Button>
  );
};

export default GoogleSignInLegacy;
